import { Inject, Injectable } from '@angular/core';
import { User } from '../model/user.model';
import { ConfigService } from '../../config/config.service';
import { Subject } from 'rxjs';
import {
  LayerPermission,
  RoleSettings,
  UserRoles,
} from 'src/assets/config/shared/user-role.settings';
import { CONSTANTS } from '../../config/constants.module';
import { HttpResponseBase } from '@angular/common/http';

/**
 * @copyright Schweizerische Bundesbahnen SBB
 * @class UserService
 * @version 0.0.1
 * @since April 2018
 * @description The UserService provides the logged user and it's roles.
 */
@Injectable({
  providedIn: 'root',
})
export abstract class UserService {
  //  There is a model that defines a User
  protected _user: User;
  // Notify user has been updated
  public onUserUpdateSubject: Subject<any>;

  constructor(
    protected configService: ConfigService,
    @Inject(CONSTANTS) public readonly CONST: any
  ) {
    this.onUserUpdateSubject = new Subject();
  }

  abstract getUserInfo(): Promise<HttpResponseBase>;

  abstract userHasRole(roleTobeChecked: string): boolean;

  /**
   * @function user
   * @returns {User}
   * @description Get the current user
   */
  public get user() {
    return this._user;
  }

  /**
   * @function setUser
   * @param {User} user
   * @description Set new logged user. This method is used for
   * developers to manipulate user-roles.
   */
  public setUser(user: User) {
    this._user = user;
  }

  /* Actions on Feature */
  public createFeaturePermitted(fkLayerId: string): boolean {
    const layerRoles = this.getRolesForLayer(fkLayerId).createFeatureRoles;
    return this.isUserPermittedFor(layerRoles);
  }

  public editFeaturePermitted(fkLayerId: string): boolean {
    const layerRoles = this.getRolesForLayer(fkLayerId).editFeatureRoles;
    return this.isUserPermittedFor(layerRoles);
  }

  public deleteFeaturePermitted(fkLayerId: string): boolean {
    const layerRoles = this.getRolesForLayer(fkLayerId).deleteFeatureRoles;
    return this.isUserPermittedFor(layerRoles);
  }

  public linkRelationTablePermitted(fkLayerId?: string): boolean {
    const layerRoles = this.getRolesForLayer(fkLayerId).linkProject;
    return this.isUserPermittedFor(layerRoles);
  }

  public unlinkRelationTablePermitted(fkLayerId?: string): boolean {
    const layerRoles = this.getRolesForLayer(fkLayerId).unlinkProject;
    return this.isUserPermittedFor(layerRoles);
  }

  /**
   * returns all allowd roles for a specific layer
   * @param fkLayerId the layerid
   */
  private getRolesForLayer(fkLayerId: string): LayerPermission {
    let returnVal: LayerPermission = new LayerPermission();
    const foundRoles = RoleSettings.layerRoles.find(layer => layer.fkLayer === fkLayerId);
    if (foundRoles) returnVal = foundRoles.roleSettings;
    return returnVal;
  }

  /**
   *  @function isUserPermittedFor
   * @param allowedRoles
   * @returns {boolean}
   * @description Validate if user has the role for the given
   * action on a specific layer.
   */
  private isUserPermittedFor(allowedRoles: UserRoles[]): boolean {
    if (!allowedRoles) return false;
    let isAllowed = false;

    this._user.roles.forEach(roleOfUser => {
      const env = this.configService.getRolesEnvironment();
      const roleFound = allowedRoles.filter(r => roleOfUser === `${r}_${env}` || roleOfUser === r);

      if (roleFound && roleFound.length > 0) isAllowed = true;
    });
    return isAllowed;
  }
}
